// Importación de diseños .ork al modelo de cohete. Ver docs/theory/ork-format.md.
import {
  DEFAULT_LAUNCH,
  ROUGHNESS,
  isaTemperaturePressure,
  materialIdForDensity,
  motorIdOf,
  updateLaunch,
  type Attached,
  type BodyTube,
  type InertComponent,
  type Deployment,
  wettedArea,
  type FinCrossSection,
  type FinSet,
  type LaunchConditions,
  type InnerTube,
  type MotorMountMark,
  type NoseCone,
  type NoseShape,
  type Parachute,
  type ParachuteLines,
  type NoseShoulder,
  type Placement,
  type Rocket,
  type StackComponent,
  type Transition,
} from '@openrocket-web/physics';
import { DEFAULT_LIMITS, readOrkXml, type ArchiveLimits } from './archive';
import { AUTO, child, childrenOf, numOf, parseXml, textOf, type El, type NumOrAuto } from './xml';

export type IssueKind = 'unsupported-component' | 'unsupported-property' | 'unsupported-setting' | 'approximation' | 'invalid' | 'info';

export interface ImportIssue {
  readonly severity: 'error' | 'warning' | 'info';
  readonly kind: IssueKind;
  /** Ruta lógica dentro del archivo, p. ej. `stage[0]/bodytube «Body tube»/shockcord «Shock cord»`. */
  readonly path: string;
  readonly message: string;
}

/** Resultados que el programa original guardó en el archivo; no los calcula este programa. */
export interface StoredResults {
  readonly maxAltitude?: number;
  readonly maxVelocity?: number;
  readonly maxAcceleration?: number;
  readonly maxMach?: number;
  readonly timeToApogee?: number;
  readonly flightTime?: number;
  readonly groundHitVelocity?: number;
  readonly launchRodVelocity?: number;
  readonly deploymentVelocity?: number;
}

export interface ImportedSimulation {
  readonly name: string;
  /** Configuración de motor con la que se simuló. */
  readonly configId?: string;
  readonly launch: LaunchConditions;
  readonly wind: { readonly speed: number; readonly direction: number; readonly turbulenceIntensity: number };
  readonly timeStep?: number;
  readonly maxTime?: number;
  readonly stored?: StoredResults;
}

export interface ImportedMotorAssignment {
  readonly motorId: string;
  readonly delay: number;
  /** Solo cuando el archivo pide un encendido que este programa modela; sin él, la ignición es automática. */
  readonly ignition?: { readonly event: 'stage' | 'launch' | 'delay'; readonly delay: number };
}

/** Configuración de motor del archivo: qué motor lleva cada portamotor, por id del tubo del diseño importado. */
export interface ImportedMotorConfig {
  readonly configId: string;
  readonly name?: string;
  readonly motors: Readonly<Record<string, ImportedMotorAssignment>>;
}

export interface OrkImport {
  /** Presente si el archivo es utilizable (con o sin contenido omitido). */
  readonly rocket: Rocket | undefined;
  /** Todas las configuraciones de motor del archivo que llevan algún motor, en el orden del archivo. */
  readonly motorConfigs: readonly ImportedMotorConfig[];
  /** Configuración cuyos motores lleva `rocket`: la predeterminada del archivo, o la primera con motor si esa no lleva. */
  readonly activeMotorConfigId?: string;
  readonly issues: readonly ImportIssue[];
  /** Subconjunto de `issues` con lo que no se pudo importar. */
  readonly unsupported: readonly ImportIssue[];
  readonly simulations: readonly ImportedSimulation[];
  /** Altura de rugosidad de la superficie (m) deducida del acabado de los componentes. */
  readonly roughness?: number;
  /** Acabado del que se dedujo la rugosidad (`normal`, `smooth`...), o `mixed` si hay varios. */
  readonly finish?: string;
  readonly formatVersion?: string;
  readonly creator?: string;
}

export interface ImportOptions {
  readonly limits?: ArchiveLimits;
  /** Configuración de motor a importar; por defecto, la marcada como predeterminada en el archivo. */
  readonly configId?: string;
}

const DEG = Math.PI / 180;

/** Acabado superficial del archivo → altura de rugosidad (m), con los valores de referencia de este programa. */
const FINISH_ROUGHNESS: Readonly<Record<string, number>> = {
  polished: ROUGHNESS.polished,
  smooth: ROUGHNESS.smooth,
  normal: ROUGHNESS.normal,
  rough: ROUGHNESS.rough,
  unfinished: ROUGHNESS.rawWood,
};
const SUPPORTED_STACK = new Set(['nosecone', 'bodytube', 'transition']);
const SUPPORTED_ATTACHED = new Set(['trapezoidfinset', 'innertube', 'parachute', 'shockcord', 'masscomponent', 'centeringring', 'bulkhead', 'engineblock', 'launchlug']);
const EPS = 1e-9;

class Ctx {
  readonly issues: ImportIssue[] = [];
  /** Portamotores leídos, para componer después las configuraciones de motor. */
  readonly mounts: Array<{ tubeId: string; mm: El; path: string }> = [];
  readonly reportedIgnition = new Set<string>();
  private counters = new Map<string, number>();
  constructor(readonly activeConfigId: string | undefined) {}

  id(prefix: string): string {
    const n = (this.counters.get(prefix) ?? 0) + 1;
    this.counters.set(prefix, n);
    return `${prefix}-${n}`;
  }
  add(severity: ImportIssue['severity'], kind: IssueKind, path: string, message: string): void {
    this.issues.push({ severity, kind, path, message });
  }
}

const label = (e: El): string => `${e.tag} «${textOf(e, 'name') ?? ''}»`;
const nameOf = (e: El, fallback: string): string => {
  const n = textOf(e, 'name');
  return n !== undefined && n !== '' ? n : fallback;
};

const MATERIAL_DEFAULT = { bulk: ['cardboard', 'cartón'], surface: ['ripstop-nylon', 'nailon ripstop'], line: ['elastic-cord', 'cordón elástico'] } as const;

function materialOf(ctx: Ctx, e: El, kind: 'bulk' | 'surface' | 'line', path: string, tag = 'material'): string {
  const m = child(e, tag);
  const density = Number(m?.attrs['density']);
  const type = m?.attrs['type'];
  if (m === undefined || !Number.isFinite(density) || density <= 0) {
    ctx.add('warning', 'approximation', path, `Material no indicado o inválido: se usa ${MATERIAL_DEFAULT[kind][1]}`);
    return MATERIAL_DEFAULT[kind][0];
  }
  if (type !== undefined && type !== kind) ctx.add('warning', 'approximation', path, `El material es de tipo «${type}» y se esperaba «${kind}»; se usa su densidad tal cual`);
  return materialIdForDensity(kind, density);
}

function need(ctx: Ctx, e: El, tag: string, path: string): number | undefined {
  const v = numOf(e, tag);
  if (typeof v === 'number') return v;
  ctx.add('error', 'invalid', path, `Falta el valor «${tag}» o no es numérico`);
  return undefined;
}

// ───────────────────────────── pila axial ─────────────────────────────

interface Draft {
  readonly el: El;
  readonly path: string;
  readonly type: 'nose-cone' | 'body-tube' | 'transition';
  length: number;
  thickness: number;
  fore: NumOrAuto; // radio delantero
  aft: NumOrAuto; // radio trasero
}

function draftStack(ctx: Ctx, items: readonly El[], basePath: string): Draft[] {
  const out: Draft[] = [];
  items.forEach((el, i) => {
    const path = `${basePath}/${label(el)}`;
    if (!SUPPORTED_STACK.has(el.tag)) {
      ctx.add('warning', 'unsupported-component', path, `Componente no soportado (se omite): ${el.tag}`);
      return;
    }
    void i;
    const length = need(ctx, el, 'length', path);
    const thickness = numOf(el, 'thickness');
    if (length === undefined) return;
    const t = typeof thickness === 'number' ? thickness : 0;
    if (el.tag === 'nosecone') out.push({ el, path, type: 'nose-cone', length, thickness: t, fore: 0, aft: numOf(el, 'aftradius') });
    else if (el.tag === 'bodytube') {
      const r = numOf(el, 'radius');
      out.push({ el, path, type: 'body-tube', length, thickness: t, fore: r, aft: r });
    } else out.push({ el, path, type: 'transition', length, thickness: t, fore: numOf(el, 'foreradius'), aft: numOf(el, 'aftradius') });
  });
  return out;
}

/** Resuelve los radios `auto` propagando desde los vecinos (ver nota del formato). */
function resolveRadii(ctx: Ctx, drafts: Draft[]): boolean {
  const known = (v: NumOrAuto): v is number => typeof v === 'number' && v > 0;
  for (let pass = 0; pass < 10; pass++) {
    let changed = false;
    drafts.forEach((d, i) => {
      const prev = drafts[i - 1];
      const next = drafts[i + 1];
      const set = (field: 'fore' | 'aft', v: number): void => {
        d[field] = v;
        changed = true;
      };
      if (d.type === 'nose-cone') {
        if (d.aft === AUTO && next !== undefined && known(next.fore)) set('aft', next.fore);
      } else if (d.type === 'body-tube') {
        if (d.aft === AUTO) {
          if (prev !== undefined && known(prev.aft)) {
            set('aft', prev.aft);
            d.fore = prev.aft;
          } else if (next !== undefined && known(next.fore)) {
            set('aft', next.fore);
            d.fore = next.fore;
          }
        }
      } else {
        if (d.fore === AUTO && prev !== undefined && known(prev.aft)) set('fore', prev.aft);
        if (d.aft === AUTO) {
          if (next !== undefined && known(next.fore)) set('aft', next.fore);
          else if (next === undefined && known(d.fore)) set('aft', d.fore);
        }
      }
    });
    if (!changed) break;
  }
  let ok = true;
  for (const d of drafts) {
    const needed = d.type === 'nose-cone' ? [d.aft] : d.type === 'body-tube' ? [d.aft] : [d.fore, d.aft];
    if (needed.some((v) => !known(v))) {
      ctx.add('error', 'invalid', d.path, 'No se pudo determinar un radio (valor automático sin vecinos con radio conocido)');
      ok = false;
    }
  }
  return ok;
}

function mapShape(ctx: Ctx, e: El, path: string): { shape: NoseShape; exponent?: number } {
  const raw = (textOf(e, 'shape') ?? 'ogive').toLowerCase();
  const param = numOf(e, 'shapeparameter');
  switch (raw) {
    case 'conical':
      return { shape: 'conical' };
    case 'ogive':
      return { shape: 'ogive' };
    case 'ellipsoid':
    case 'elliptical':
      return { shape: 'elliptical' };
    case 'power': {
      const n = typeof param === 'number' && param > 0 && param <= 1 ? param : 0.5;
      if (typeof param !== 'number' || n !== param) ctx.add('warning', 'approximation', path, `Parámetro de la serie de potencia no válido: se usa ${n}`);
      return { shape: 'power', exponent: n };
    }
    case 'parabolic':
      if (typeof param === 'number' && Math.abs(param - 1) > 0.01) ctx.add('warning', 'approximation', path, `Ojiva parabólica con parámetro ${param}: se aproxima con el caso K = 1`);
      return { shape: 'parabolic' };
    default:
      ctx.add('warning', 'approximation', path, `Forma «${raw}» no soportada: se aproxima con una ojiva tangente`);
      return { shape: 'ogive' };
  }
}

/** Hombro de la ojiva (`aftshoulder*`): cilindro hueco con tapa opcional; un espesor no válido lo deja macizo. */
function buildShoulder(ctx: Ctx, e: El, path: string): NoseShoulder | undefined {
  const length = numOf(e, 'aftshoulderlength');
  if (typeof length !== 'number' || !(length > 0)) return undefined;
  const radius = numOf(e, 'aftshoulderradius');
  if (typeof radius !== 'number' || !(radius > 0)) {
    ctx.add('warning', 'unsupported-property', path, 'El hombro del cono no tiene un radio válido: se omite');
    return undefined;
  }
  const t = numOf(e, 'aftshoulderthickness');
  const valid = typeof t === 'number' && t > 0;
  if (!valid) ctx.add('info', 'approximation', path, 'El hombro del cono no tiene un espesor válido: se importa macizo');
  // La tapa no se cuenta: con ella la masa de lanzamiento de los ejemplos queda entre 1 y 2 g por encima de la guardada
  // por el programa original, y sin ella coincide (ver docs/validation.md).
  if (textOf(e, 'aftshouldercapped') === 'true') ctx.add('info', 'approximation', path, 'La tapa del hombro no se cuenta en la masa: así coincide con la masa guardada en el archivo');
  return { length, diameter: 2 * radius, thickness: valid ? Math.min(t, radius) : radius, capped: false };
}

function buildStackComponent(ctx: Ctx, d: Draft): StackComponent {
  const e = d.el;
  const name = nameOf(e, d.type);
  const aft = d.aft as number;
  if (d.type === 'nose-cone') {
    const { shape, exponent } = mapShape(ctx, e, d.path);
    const shoulder = buildShoulder(ctx, e, d.path);
    const nose: NoseCone = {
      id: ctx.id('nose'),
      name,
      type: 'nose-cone',
      shape,
      ...(exponent === undefined ? {} : { powerExponent: exponent }),
      length: d.length,
      baseDiameter: 2 * aft,
      wallThickness: d.thickness > 0 ? d.thickness : 0.001,
      filled: d.thickness >= aft,
      material: materialOf(ctx, e, 'bulk', d.path),
      ...(shoulder === undefined ? {} : { shoulder }),
    };
    return nose;
  }
  if (d.type === 'body-tube') {
    const id = ctx.id('tube');
    const mm = child(e, 'motormount');
    if (mm !== undefined) ctx.mounts.push({ tubeId: id, mm, path: d.path });
    const tube: BodyTube = {
      id,
      name,
      type: 'body-tube',
      length: d.length,
      outerDiameter: 2 * aft,
      wallThickness: d.thickness,
      material: materialOf(ctx, e, 'bulk', d.path),
      children: [],
      ...(mm === undefined ? {} : { motorMount: mountMark(ctx, mm, d.path, 0) }),
    };
    return tube;
  }
  const shape = (textOf(e, 'shape') ?? 'conical').toLowerCase();
  if (shape !== 'conical') ctx.add('warning', 'approximation', d.path, `Transición de forma «${shape}»: se aproxima con un perfil cónico`);
  const tr: Transition = {
    id: ctx.id('transition'),
    name,
    type: 'transition',
    length: d.length,
    foreDiameter: 2 * (d.fore as number),
    aftDiameter: 2 * aft,
    wallThickness: d.thickness,
    material: materialOf(ctx, e, 'bulk', d.path),
    children: [],
  };
  return tr;
}

// ───────────────────────────── componentes adjuntos ─────────────────────────────

interface RawOffset {
  /** Desplazamiento en su propio anclaje (puede quedar fuera del padre). */
  readonly offset: number;
  readonly fromAft: boolean;
}

/** Lee `axialoffset` como un desplazamiento respecto al padre, sin ajustarlo. */
function resolveOffset(ctx: Ctx, e: El, childLength: number, parentLength: number, parentX0: number, path: string): RawOffset {
  const ao = child(e, 'axialoffset');
  const method = (ao?.attrs['method'] ?? child(e, 'position')?.attrs['type'] ?? 'top').toLowerCase();
  const v = Number(ao?.text ?? textOf(e, 'position') ?? 0);
  const value = Number.isFinite(v) ? v : 0;

  // `bottom` se expresa respecto al borde trasero; el resto, respecto al delantero. Se calcula el
  // desplazamiento en su propio anclaje para no perder exactitud por aritmética en coma flotante.
  switch (method) {
    case 'bottom':
      return { fromAft: true, offset: -value }; // un valor positivo sobresale por detrás; uno negativo adelanta el componente
    case 'top':
      return { fromAft: false, offset: value };
    case 'middle':
      return { fromAft: false, offset: (parentLength - childLength) / 2 + value };
    case 'absolute':
      return { fromAft: false, offset: value - parentX0 };
    default:
      ctx.add('warning', 'approximation', path, `Método de posición «${method}» no soportado: se coloca al inicio del cuerpo`);
      return { fromAft: false, offset: 0 };
  }
}

/** Ajusta un desplazamiento al interior del padre e informa de lo que sobresale. */
function clampOffset(ctx: Ctx, raw: RawOffset, childLength: number, parentLength: number, path: string): number {
  const maxOffset = Math.max(parentLength - childLength, 0);
  const clamped = Math.min(Math.max(raw.offset, 0), maxOffset);
  if (Math.abs(clamped - raw.offset) > EPS) {
    const outside = raw.fromAft ? clamped - raw.offset : raw.offset - clamped; // positivo = sobresale por detrás
    ctx.add('warning', 'approximation', path, `El componente queda fuera del cuerpo (${outside > 0 ? '+' : ''}${outside.toFixed(4)} m): se ajusta a ras`);
  }
  return clamped;
}

/** Convierte `axialoffset` a una posición de nuestro modelo, ajustando lo que queda fuera del padre. */
function placement(ctx: Ctx, e: El, childLength: number, parentLength: number, parentX0: number, path: string): Placement {
  const raw = resolveOffset(ctx, e, childLength, parentLength, parentX0, path);
  return { anchor: raw.fromAft ? 'aft' : 'front', offset: clampOffset(ctx, raw, childLength, parentLength, path) };
}

function buildFins(ctx: Ctx, e: El, parentLength: number, parentX0: number, path: string): FinSet | undefined {
  const root = need(ctx, e, 'rootchord', path);
  const tip = need(ctx, e, 'tipchord', path);
  const sweep = need(ctx, e, 'sweeplength', path);
  const span = need(ctx, e, 'height', path);
  const thickness = need(ctx, e, 'thickness', path);
  const countRaw = numOf(e, 'instancecount') ?? numOf(e, 'fincount');
  if (root === undefined || tip === undefined || sweep === undefined || span === undefined || thickness === undefined || typeof countRaw !== 'number') {
    if (typeof countRaw !== 'number') ctx.add('error', 'invalid', path, 'Falta el número de aletas');
    return undefined;
  }
  const cant = numOf(e, 'cant');
  if (typeof cant === 'number' && Math.abs(cant) > 1e-9) ctx.add('warning', 'unsupported-property', path, `La inclinación (cant) de ${cant} rad se ignora`);
  const section = textOf(e, 'crosssection');
  const crossSection: FinCrossSection = section === 'square' ? 'square' : section === 'rounded' ? 'rounded' : 'airfoil';
  if (section === 'airfoil') ctx.add('info', 'approximation', path, 'El perfil aerodinámico se trata como borde redondeado (el espesor no cambia la forma)');
  else if (section !== undefined && section !== 'square' && section !== 'rounded') ctx.add('info', 'approximation', path, `Sección «${section}» desconocida: se trata como redondeada`);
  const fillet = numOf(e, 'filletradius');
  if (typeof fillet === 'number' && fillet > 0) ctx.add('info', 'unsupported-property', path, 'El filete de unión se ignora');
  return {
    id: ctx.id('fins'),
    name: nameOf(e, 'Aletas'),
    type: 'fin-set',
    finCount: countRaw,
    rootChord: root,
    tipChord: tip,
    span,
    sweep,
    thickness,
    ...(section === undefined ? {} : { crossSection }),
    material: materialOf(ctx, e, 'bulk', path),
    position: placement(ctx, e, root, parentLength, parentX0, path),
  };
}

/** Todos los descendientes con esa etiqueta, sin entrar en los que ya coinciden. */
function deepFind(e: El, tag: string): El[] {
  return e.children.flatMap((c) => (c.tag === tag ? [c] : deepFind(c, tag)));
}

function pickMotor(ctx: Ctx, mm: El): El | undefined {
  return childrenOf(mm, 'motor').find((m) => m.attrs['configid'] === ctx.activeConfigId);
}

/** Motor y retardo de un `<motor>` del archivo; con `ctx` informa de lo que no se puede importar. */
function motorSelection(ctx: Ctx | undefined, motorEl: El, path: string): MotorMountMark['motor'] {
  const manufacturer = textOf(motorEl, 'manufacturer') ?? '';
  const designation = textOf(motorEl, 'designation') ?? '';
  const delayText = textOf(motorEl, 'delay') ?? '';
  const delay = Number(delayText);
  if (designation === '') {
    ctx?.add('warning', 'invalid', path, 'El motor no tiene designación: se importa el soporte sin motor');
    return undefined;
  }
  if (!Number.isFinite(delay)) ctx?.add('info', 'approximation', path, `Retardo «${delayText}»: sin carga de eyección, se usa 0 s`);
  return { motorId: motorIdOf(manufacturer, designation), ejectionDelay: Number.isFinite(delay) && delay >= 0 ? delay : 0 };
}

/** Marca de portamotor de un tubo: voladizo (más lo que `extraOverhang` añada) y motor de la configuración activa. */
function mountMark(ctx: Ctx, mm: El, path: string, extraOverhang: number): MotorMountMark {
  const overhangRaw = numOf(mm, 'overhang');
  const overhang = (typeof overhangRaw === 'number' && overhangRaw > 0 ? overhangRaw : 0) + extraOverhang;
  const motorEl = pickMotor(ctx, mm);
  let motor: MotorMountMark['motor'];
  if (motorEl !== undefined) motor = motorSelection(ctx, motorEl, path);
  else ctx.add('info', 'info', path, 'El soporte no tiene motor en la configuración por defecto');
  return { ...(overhang > 0 ? { overhang } : {}), ...(motor === undefined ? {} : { motor }) };
}

/** Tubo interior; si el archivo le da soporte de motor, queda marcado como portamotor. */
function buildInnerTube(ctx: Ctx, e: El, parentLength: number, parentX0: number, path: string): InnerTube | undefined {
  const mm = child(e, 'motormount');
  const cluster = textOf(e, 'clusterconfiguration');
  if (cluster !== undefined && cluster !== 'single') {
    ctx.add('warning', 'unsupported-component', path, mm === undefined ? `Tubo interior en configuración «${cluster}» (cluster): no soportado, se omite` : `Soporte de motor en configuración «${cluster}» (cluster): no soportado, se omite`);
    return undefined;
  }
  const length = need(ctx, e, 'length', path);
  const outer = numOf(e, 'outerradius');
  const thickness = numOf(e, 'thickness');
  if (length === undefined || typeof outer !== 'number' || typeof thickness !== 'number') {
    ctx.add('error', 'invalid', path, 'Faltan dimensiones del tubo del motor (length, outerradius, thickness)');
    return undefined;
  }
  // Un soporte que sobresale por detrás del cuerpo se ajusta a ras; para no mover el motor, lo que sobresale se suma al voladizo.
  const raw = resolveOffset(ctx, e, length, parentLength, parentX0, path);
  const behind = mm === undefined ? 0 : raw.fromAft ? Math.max(-raw.offset, 0) : Math.max(raw.offset - Math.max(parentLength - length, 0), 0);
  if (behind > EPS) ctx.add('info', 'approximation', path, `El soporte sobresale ${behind.toFixed(4)} m por detrás del cuerpo: el motor conserva su posición`);
  const id = ctx.id(mm === undefined ? 'inner' : 'mount');
  if (mm !== undefined) ctx.mounts.push({ tubeId: id, mm, path });

  return {
    id,
    name: nameOf(e, mm === undefined ? 'Tubo interior' : 'Soporte de motor'),
    type: 'inner-tube',
    length,
    innerDiameter: 2 * (outer - thickness),
    wallThickness: thickness,
    material: materialOf(ctx, e, 'bulk', path),
    position: { anchor: raw.fromAft ? 'aft' : 'front', offset: clampOffset(ctx, raw, length, parentLength, path) },
    ...(mm === undefined ? {} : { motorMount: mountMark(ctx, mm, path, behind > EPS ? behind : 0) }),
  };
}

/** Ignición de un motor en una configuración: solo `launch` se importa; lo demás se trata como automático. */
function ignitionOf(ctx: Ctx, mm: El, configId: string, path: string): ImportedMotorAssignment['ignition'] {
  const cfg = childrenOf(mm, 'ignitionconfiguration').find((c) => c.attrs['configid'] === configId);
  const event = (textOf(cfg, 'ignitionevent') ?? textOf(mm, 'ignitionevent') ?? 'automatic').toLowerCase();
  const delayRaw = numOf(cfg ?? mm, 'ignitiondelay');
  const delay = typeof delayRaw === 'number' && delayRaw > 0 ? delayRaw : 0;
  if (event === 'automatic') return undefined;
  if (event === 'launch') return { event: 'launch', delay };
  const key = `${path}|${event}`;
  if (!ctx.reportedIgnition.has(key)) {
    ctx.reportedIgnition.add(key);
    ctx.add('info', 'unsupported-setting', path, `Evento de ignición «${event}» no soportado: se usa la ignición automática`);
  }
  return undefined;
}

/** Configuraciones de motor del archivo que llevan algún motor, con el motor de cada portamotor leído. */
function buildMotorConfigs(ctx: Ctx, configEls: readonly El[]): ImportedMotorConfig[] {
  const ids: string[] = [];
  const add = (id: string | undefined): void => {
    if (id !== undefined && !ids.includes(id)) ids.push(id);
  };
  for (const c of configEls) add(c.attrs['configid']);
  for (const m of ctx.mounts) for (const x of childrenOf(m.mm, 'motor')) add(x.attrs['configid']);

  const out: ImportedMotorConfig[] = [];
  for (const configId of ids) {
    const motors: Record<string, ImportedMotorAssignment> = {};
    for (const m of ctx.mounts) {
      const motorEl = childrenOf(m.mm, 'motor').find((x) => x.attrs['configid'] === configId);
      const sel = motorEl === undefined ? undefined : motorSelection(undefined, motorEl, m.path);
      if (sel === undefined) continue;
      const ignition = ignitionOf(ctx, m.mm, configId, m.path);
      motors[m.tubeId] = { motorId: sel.motorId, delay: sel.ejectionDelay, ...(ignition === undefined ? {} : { ignition }) };
    }
    if (Object.keys(motors).length === 0) continue;
    const name = textOf(configEls.find((c) => c.attrs['configid'] === configId), 'name');
    out.push({ configId, ...(name === undefined || name === '' ? {} : { name }), motors });
  }
  return out;
}

/** Cuerdas del paracaídas: una longitud `auto` equivale al diámetro del paracaídas. */
function buildLines(ctx: Ctx, e: El, diameter: number, path: string): ParachuteLines | undefined {
  const count = numOf(e, 'linecount');
  if (typeof count !== 'number' || !(count > 0)) return undefined;
  const raw = numOf(e, 'linelength');
  const length = typeof raw === 'number' ? raw : diameter;
  if (raw === AUTO) ctx.add('info', 'approximation', path, 'Longitud de cuerdas automática: se usa el diámetro del paracaídas');
  if (!(length > 0)) return undefined;
  return { count, length, material: materialOf(ctx, e, 'line', path, 'linematerial') };
}

function buildParachute(ctx: Ctx, e: El, parentLength: number, parentX0: number, path: string): Parachute | undefined {
  const diameter = need(ctx, e, 'diameter', path);
  const packed = need(ctx, e, 'packedlength', path);
  if (diameter === undefined || packed === undefined) return undefined;
  const cdRaw = numOf(e, 'cd');
  const cd = typeof cdRaw === 'number' && cdRaw > 0 ? cdRaw : 0.8;
  if (typeof cdRaw !== 'number') ctx.add('info', 'approximation', path, 'Coeficiente de arrastre automático: se usa 0,8');

  const event = (textOf(e, 'deployevent') ?? 'apogee').toLowerCase();
  const altitude = numOf(e, 'deployaltitude');
  let deployment: Deployment;
  if (event === 'apogee') deployment = { event: 'apogee' };
  else if (event === 'ejection') deployment = { event: 'ejection' };
  else if (event === 'altitude' && typeof altitude === 'number' && altitude > 0) deployment = { event: 'altitude', altitude };
  else {
    ctx.add('warning', 'approximation', path, `Evento de despliegue «${event}» no soportado: se usa el apogeo`);
    deployment = { event: 'apogee' };
  }
  const delay = numOf(e, 'deploydelay');
  if (typeof delay === 'number' && delay > 0) ctx.add('warning', 'unsupported-property', path, `El retardo de despliegue de ${delay} s se ignora`);
  const lines = buildLines(ctx, e, diameter, path);

  return {
    id: ctx.id('chute'),
    name: nameOf(e, 'Paracaídas'),
    type: 'parachute',
    diameter,
    dragCoefficient: cd,
    material: materialOf(ctx, e, 'surface', path),
    packedLength: packed,
    deployment,
    ...(lines === undefined ? {} : { lines }),
    position: placement(ctx, e, packed, parentLength, parentX0, path),
  };
}

// ───────────────────────────── componentes inertes ─────────────────────────────

/** Tubo interior colocado en el padre: sirve para resolver radios automáticos de anillos y topes. */
interface HostTube {
  /** Borde delantero respecto al padre según el archivo, sin ajustar (m). */
  readonly front: number;
  readonly length: number;
  readonly outerRadius: number;
  readonly innerRadius: number;
}

interface InertParent {
  readonly length: number;
  readonly x0: number;
  /** Radio interior del cuerpo que contiene al componente. */
  readonly innerRadius: number;
  readonly tubes: readonly HostTube[];
}

const INERT_ID: Readonly<Record<string, string>> = { shockcord: 'cord', masscomponent: 'mass', centeringring: 'ring', bulkhead: 'bulkhead', engineblock: 'block', launchlug: 'lug' };
const INERT_NAME: Readonly<Record<string, string>> = { shockcord: 'Cordón', masscomponent: 'Masa', centeringring: 'Anillo de centrado', bulkhead: 'Disco', engineblock: 'Retén de motor', launchlug: 'Soporte para guía' };

/** Tubo interior sencillo (sin cluster): el mismo criterio con el que `buildInnerTube` lo acepta. */
function hostTubeOf(sub: El, parentLength: number, parentX0: number): HostTube | undefined {
  if (sub.tag !== 'innertube') return undefined;
  const cluster = textOf(sub, 'clusterconfiguration');
  const length = numOf(sub, 'length');
  const outer = numOf(sub, 'outerradius');
  const thickness = numOf(sub, 'thickness');
  if ((cluster !== undefined && cluster !== 'single') || typeof length !== 'number' || typeof outer !== 'number' || typeof thickness !== 'number') return undefined;
  // Posición real en el archivo, sin ajustar: lo que cuelga del tubo conserva su sitio aunque el tubo sobresalga.
  const raw = resolveOffset(new Ctx(undefined), sub, length, parentLength, parentX0, '');
  return { front: raw.fromAft ? parentLength - length - raw.offset : raw.offset, length, outerRadius: outer, innerRadius: outer - thickness };
}

/** Lee un componente sin aerodinámica propia; si está mal formado lo omite con un aviso. */
function buildInert(ctx: Ctx, e: El, parent: InertParent, host: HostTube | undefined, path: string): InertComponent | undefined {
  const length = numOf(e, e.tag === 'shockcord' || e.tag === 'masscomponent' ? 'packedlength' : 'length');
  if (typeof length !== 'number' || !(length > 0)) {
    ctx.add('warning', 'unsupported-component', path, 'Falta la longitud del componente: se omite');
    return undefined;
  }
  const count = numOf(e, 'instancecount');
  if (typeof count === 'number' && count > 1) ctx.add('warning', 'unsupported-property', path, `Hay ${count} instancias: solo se importa la primera`);
  const id = ctx.id(INERT_ID[e.tag]!);
  const name = nameOf(e, INERT_NAME[e.tag]!);

  // Un componente dentro de un tubo interior se coloca relativo a ese tubo y se sube al tubo de cuerpo.
  const position = (): Placement => {
    if (host === undefined) return placement(ctx, e, length, parent.length, parent.x0, path);
    const raw = resolveOffset(ctx, e, length, host.length, parent.x0 + host.front, path);
    const inTube = raw.fromAft ? host.length - length - raw.offset : raw.offset;
    return { anchor: 'front', offset: clampOffset(ctx, { fromAft: false, offset: host.front + inTube }, length, parent.length, path) };
  };

  switch (e.tag) {
    case 'shockcord': {
      const cordLength = numOf(e, 'cordlength');
      if (typeof cordLength !== 'number' || cordLength < 0) {
        ctx.add('warning', 'unsupported-component', path, 'Falta la longitud del cordón: se omite');
        return undefined;
      }
      return { id, name, type: 'shock-cord', length, cordLength, material: materialOf(ctx, e, 'line', path), position: position() };
    }
    case 'masscomponent': {
      const mass = numOf(e, 'mass');
      if (typeof mass !== 'number' || mass < 0) {
        ctx.add('warning', 'unsupported-component', path, 'Falta la masa del componente: se omite');
        return undefined;
      }
      return { id, name, type: 'mass-component', length, mass, position: position() };
    }
    case 'launchlug': {
      const radius = numOf(e, 'radius');
      const thickness = numOf(e, 'thickness');
      if (typeof radius !== 'number' || typeof thickness !== 'number') {
        ctx.add('warning', 'unsupported-component', path, 'Faltan el radio o el espesor del soporte para guía: se omite');
        return undefined;
      }
      return { id, name, type: 'launch-lug', length, outerDiameter: 2 * radius, wallThickness: thickness, material: materialOf(ctx, e, 'bulk', path), position: position() };
    }
    default: {
      // Anillo, disco y retén: coronas. Los radios automáticos salen del cuerpo o del tubo interior que atraviesan.
      const pos = position();
      const front = pos.anchor === 'front' ? pos.offset : parent.length - pos.offset - length;
      const through = parent.tubes.find((t) => front + length / 2 >= t.front - EPS && front + length / 2 <= t.front + t.length + EPS);
      const outerRaw = numOf(e, 'outerradius');
      const outer = typeof outerRaw === 'number' ? outerRaw : (host?.innerRadius ?? parent.innerRadius);
      let inner = 0;
      if (e.tag === 'centeringring') {
        const innerRaw = numOf(e, 'innerradius');
        inner = typeof innerRaw === 'number' ? innerRaw : (through?.outerRadius ?? 0);
      } else if (e.tag === 'engineblock') {
        const thickness = numOf(e, 'thickness');
        inner = typeof thickness === 'number' ? Math.max(outer - thickness, 0) : 0;
      }
      const type = e.tag === 'centeringring' ? 'centering-ring' : e.tag === 'bulkhead' ? 'bulkhead' : 'engine-block';
      return { id, name, type, length, outerDiameter: 2 * outer, innerDiameter: 2 * inner, material: materialOf(ctx, e, 'bulk', path), position: pos };
    }
  }
}

function buildChildren(ctx: Ctx, parent: El, parentPath: string, parentLength: number, parentX0: number, parentInnerRadius: number): Attached[] {
  const out: Attached[] = [];
  const subs = parent.children.filter((c) => c.tag === 'subcomponents').flatMap((c) => c.children);
  const tubes = subs.flatMap((sub) => {
    const t = hostTubeOf(sub, parentLength, parentX0);
    return t === undefined ? [] : [t];
  });
  const inertParent: InertParent = { length: parentLength, x0: parentX0, innerRadius: parentInnerRadius, tubes };
  const unsupported = (sub: El, path: string): void => ctx.add('warning', 'unsupported-component', path, `Componente no soportado (se omite): ${sub.tag}`);

  for (const sub of subs) {
    const path = `${parentPath}/${label(sub)}`;
    if (!SUPPORTED_ATTACHED.has(sub.tag)) {
      unsupported(sub, path);
      continue;
    }
    if (sub.tag in INERT_ID) {
      const built = buildInert(ctx, sub, inertParent, undefined, path);
      if (built !== undefined) out.push(built);
      continue;
    }
    const built = sub.tag === 'trapezoidfinset' ? buildFins(ctx, sub, parentLength, parentX0, path) : sub.tag === 'innertube' ? buildInnerTube(ctx, sub, parentLength, parentX0, path) : buildParachute(ctx, sub, parentLength, parentX0, path);
    if (built !== undefined) out.push(built);
    if (sub.tag !== 'innertube') continue;
    // Lo que cuelga del tubo interior: el retén de motor sube al tubo de cuerpo.
    const host = built === undefined ? undefined : hostTubeOf(sub, parentLength, parentX0);
    for (const nested of sub.children.filter((c) => c.tag === 'subcomponents').flatMap((c) => c.children)) {
      const nestedPath = `${path}/${label(nested)}`;
      if (host === undefined || nested.tag !== 'engineblock') {
        unsupported(nested, nestedPath);
        continue;
      }
      const block = buildInert(ctx, nested, inertParent, host, nestedPath);
      if (block !== undefined) out.push(block);
    }
  }
  return out;
}

// ───────────────────────────── simulaciones guardadas ─────────────────────────────

const attrNum = (e: El | undefined, name: string): number | undefined => {
  const v = Number(e?.attrs[name]);
  return e?.attrs[name] !== undefined && Number.isFinite(v) ? v : undefined;
};

function storedResults(sim: El): StoredResults | undefined {
  const fd = child(sim, 'flightdata');
  if (fd === undefined) return undefined;
  const r: Record<string, number> = {};
  const map: Array<[string, string]> = [
    ['maxaltitude', 'maxAltitude'], ['maxvelocity', 'maxVelocity'], ['maxacceleration', 'maxAcceleration'], ['maxmach', 'maxMach'],
    ['timetoapogee', 'timeToApogee'], ['flighttime', 'flightTime'], ['groundhitvelocity', 'groundHitVelocity'],
    ['launchrodvelocity', 'launchRodVelocity'], ['deploymentvelocity', 'deploymentVelocity'],
  ];
  for (const [attr, key] of map) {
    const v = attrNum(fd, attr);
    if (v !== undefined) r[key] = v;
  }
  return Object.keys(r).length > 0 ? (r as StoredResults) : undefined;
}

function buildSimulation(ctx: Ctx, sim: El, index: number): ImportedSimulation | undefined {
  const name = nameOf(sim, `Simulación ${index + 1}`);
  const path = `simulation «${name}»`;
  const c = child(sim, 'conditions');
  if (c === undefined) {
    ctx.add('warning', 'invalid', path, 'La simulación no tiene condiciones (se omite)');
    return undefined;
  }
  const n = (tag: string): number | undefined => {
    const v = numOf(c, tag);
    return typeof v === 'number' ? v : undefined;
  };

  const altitude = n('launchaltitude') ?? 0;
  const isa = isaTemperaturePressure(altitude);
  const patch: Partial<LaunchConditions> = {
    siteAltitude: altitude,
    temperature: isa.temperature,
    pressure: isa.pressure,
    ...(n('launchlatitude') === undefined ? {} : { latitude: n('launchlatitude')! * DEG }),
    ...(n('launchlongitude') === undefined ? {} : { longitude: n('launchlongitude')! * DEG }),
    ...(n('launchrodlength') === undefined ? {} : { rodLength: n('launchrodlength')! }),
    ...(n('launchrodangle') === undefined ? {} : { rodAngle: n('launchrodangle')! * DEG }),
    ...(n('launchroddirection') === undefined ? {} : { rodDirection: n('launchroddirection')! * DEG }),
  };
  const { conditions: launch, errors } = updateLaunch(DEFAULT_LAUNCH, patch);
  for (const [field, message] of Object.entries(errors)) ctx.add('warning', 'invalid', path, `Condición de lanzamiento inválida (${field}): ${message}. Se usa el valor por defecto`);

  const atmosphere = child(c, 'atmosphere')?.attrs['model'];
  if (atmosphere !== undefined && atmosphere !== 'isa') ctx.add('warning', 'unsupported-setting', path, `Atmósfera «${atmosphere}» no soportada: se usa la ISA`);
  const windType = textOf(c, 'windmodeltype');
  if (windType !== undefined && windType !== 'Average') ctx.add('warning', 'unsupported-setting', path, `Modelo de viento «${windType}» no soportado: se usa el viento medio`);
  const geodetic = textOf(c, 'geodeticmethod');
  if (geodetic !== undefined && geodetic !== 'flat') ctx.add('info', 'unsupported-setting', path, `Método geodésico «${geodetic}» no soportado: se usa tierra plana`);
  const calculator = textOf(sim, 'calculator');
  if (calculator !== undefined && calculator !== 'BarrowmanCalculator') ctx.add('warning', 'unsupported-setting', path, `Calculadora aerodinámica «${calculator}» no soportada`);
  const simulator = textOf(sim, 'simulator');
  if (simulator !== undefined && simulator !== 'RK4Simulator') ctx.add('info', 'unsupported-setting', path, `Método de integración «${simulator}»: este programa usa RK4`);

  const configId = textOf(c, 'configid');
  const timeStep = n('timestep');
  const maxTime = n('maxtime');
  const stored = storedResults(sim);
  return {
    name,
    ...(configId === undefined ? {} : { configId }),
    launch,
    wind: { speed: n('windaverage') ?? 0, direction: n('winddirection') ?? 0, turbulenceIntensity: n('windturbulence') ?? 0 },
    ...(timeStep === undefined ? {} : { timeStep }),
    ...(maxTime === undefined ? {} : { maxTime }),
    ...(stored === undefined ? {} : { stored }),
  };
}

// ───────────────────────────── entrada principal ─────────────────────────────

function result(ctx: Ctx, rocket: Rocket | undefined, simulations: ImportedSimulation[], meta: { formatVersion?: string; creator?: string; finish?: string; roughness?: number }, motorConfigs: readonly ImportedMotorConfig[] = []): OrkImport {
  const roughness = meta.roughness;
  return {
    ...(roughness === undefined ? {} : { roughness, finish: meta.finish }),
    rocket,
    motorConfigs,
    ...(ctx.activeConfigId !== undefined && motorConfigs.some((c) => c.configId === ctx.activeConfigId) ? { activeMotorConfigId: ctx.activeConfigId } : {}),
    issues: ctx.issues,
    unsupported: ctx.issues.filter((i) => i.kind.startsWith('unsupported')),
    simulations,
    ...(meta.formatVersion === undefined ? {} : { formatVersion: meta.formatVersion }),
    ...(meta.creator === undefined ? {} : { creator: meta.creator }),
  };
}

/** Lee un diseño .ork. Nunca lanza: los problemas se devuelven en `issues`. */
export function importOrk(bytes: Uint8Array, options: ImportOptions = {}): OrkImport {
  const failEarly = (message: string): OrkImport => ({ rocket: undefined, motorConfigs: [], issues: [{ severity: 'error', kind: 'invalid', path: '', message }], unsupported: [], simulations: [] });

  const archive = readOrkXml(bytes, options.limits ?? DEFAULT_LIMITS);
  if (!archive.ok) return failEarly(archive.error);
  const parsed = parseXml(archive.xml);
  if (!parsed.ok) return failEarly(parsed.error);
  const root = parsed.root;
  if (root.tag !== 'openrocket') return failEarly('El documento no es un diseño de OpenRocket (falta <openrocket>)');
  const rocketEl = child(root, 'rocket');
  if (rocketEl === undefined) return failEarly('El diseño no contiene un cohete (<rocket>)');

  const meta = { ...(root.attrs['version'] === undefined ? {} : { formatVersion: root.attrs['version'] }), ...(root.attrs['creator'] === undefined ? {} : { creator: root.attrs['creator'] }) };
  const configs = childrenOf(rocketEl, 'motorconfiguration');
  const declaredConfigId = options.configId ?? (configs.find((c) => c.attrs['default'] === 'true') ?? configs[0])?.attrs['configid'];
  // La configuración que lleva el diseño: la pedida, o la primera con motor si esa no lleva ninguno (por ejemplo, la predeterminada vacía).
  const motorConfigIds = deepFind(rocketEl, 'motormount').flatMap((m) => childrenOf(m, 'motor').flatMap((x) => (x.attrs['configid'] === undefined ? [] : [x.attrs['configid']])));
  const activeConfigId = declaredConfigId !== undefined && motorConfigIds.includes(declaredConfigId) ? declaredConfigId : (motorConfigIds[0] ?? declaredConfigId);
  const ctx = new Ctx(activeConfigId);

  const stages = childrenOf(child(rocketEl, 'subcomponents'), 'stage');
  if (stages.length === 0) {
    ctx.add('error', 'invalid', 'rocket', 'El cohete no tiene etapas');
    return result(ctx, undefined, [], meta);
  }
  stages.slice(1).forEach((s, i) => ctx.add('warning', 'unsupported-component', `stage[${i + 1}]`, `Etapa adicional «${textOf(s, 'name') ?? ''}» no soportada (solo se importa la primera etapa)`));

  const stageEl = stages[0]!;
  const stagePath = 'stage[0]';
  const items = childrenOf(stageEl, 'subcomponents').flatMap((s) => s.children);
  const drafts = draftStack(ctx, items, stagePath);
  if (drafts.length === 0) {
    ctx.add('error', 'invalid', stagePath, 'La etapa no contiene cono, tubos ni transiciones soportados');
    return result(ctx, undefined, [], meta);
  }
  if (ctx.issues.some((i) => i.severity === 'error') || !resolveRadii(ctx, drafts)) return result(ctx, undefined, [], meta);

  const stack: StackComponent[] = [];
  let x0 = 0;
  for (const d of drafts) {
    const base = buildStackComponent(ctx, d);
    if (base.type === 'nose-cone') stack.push(base);
    else {
      const inner = base.type === 'body-tube' ? base.outerDiameter / 2 - base.wallThickness : Math.min(base.foreDiameter, base.aftDiameter) / 2 - base.wallThickness;
      stack.push({ ...base, children: buildChildren(ctx, d.el, d.path, d.length, x0, inner) });
    }
    x0 += d.length;
  }
  if (ctx.issues.some((i) => i.severity === 'error')) return result(ctx, undefined, [], meta);

  const rocket: Rocket = { name: nameOf(rocketEl, 'Cohete importado'), stack };
  // Rugosidad equivalente: media de las alturas de rugosidad ponderada por la superficie mojada de cada componente.
  const finishes = drafts.map((d) => textOf(d.el, 'finish'));
  let weighted = 0;
  let area = 0;
  stack.forEach((c, i) => {
    const f = finishes[i];
    if (f === undefined || !(f in FINISH_ROUGHNESS)) return;
    const a = wettedArea(c);
    weighted += a * FINISH_ROUGHNESS[f]!;
    area += a;
  });
  const known = finishes.filter((f): f is string => f !== undefined && f in FINISH_ROUGHNESS);
  const distinct = [...new Set(known)];
  const mainFinish = textOf(drafts.find((d) => d.type === 'body-tube')?.el, 'finish') ?? known[0];
  if (distinct.length > 1) ctx.add('info', 'approximation', stagePath, `Los componentes tienen acabados distintos (${distinct.join(', ')}): se usa la rugosidad media ponderada por superficie`);
  const meta2 = area > 0 ? { ...meta, finish: distinct.length > 1 ? 'mixed' : mainFinish!, roughness: weighted / area } : meta;
  const sims = childrenOf(child(root, 'simulations'), 'simulation').flatMap((s, i) => {
    const built = buildSimulation(ctx, s, i);
    return built === undefined ? [] : [built];
  });
  return result(ctx, rocket, sims, meta2, buildMotorConfigs(ctx, configs));
}
